import {
  Component,
  model,
  input,
  contentChildren,
  effect,
  untracked,
  computed,
  ModelSignal,
  InputSignal,
  Signal
} from '@angular/core';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { StepComponent } from './step.component';
import { STEPS_HOST } from './steps.contract';

@Component({
  selector: 'td-wb-steps',
  templateUrl: 'steps.component.html',
  styleUrls: ['steps.component.scss'],
  imports: [TranslatePipe],
  providers: [{ provide: STEPS_HOST, useExisting: StepsComponent }]
})
export class StepsComponent {
  activeStepId: ModelSignal<string | number | undefined> = model<string | number>();
  disabled: InputSignal<boolean> = input<boolean>(false);

  steps: Signal<ReadonlyArray<StepComponent>> = contentChildren(StepComponent);

  activeStepIndex: Signal<number> = computed(() => {
    const steps: ReadonlyArray<StepComponent> = this.steps();
    const activeStepId: string | number | undefined = this.activeStepId();
    return steps.findIndex((s: StepComponent) => s.stepId() === activeStepId);
  });

  private nextId: number = 1;

  constructor() {
    // effect: assigns auto-generated IDs to new StepComponent children and initialises
    // the active step — StepComponent.setStepId() is an imperative child-component API
    // that cannot be expressed as a template binding from this host.
    effect(() => {
      const steps: ReadonlyArray<StepComponent> = this.steps();
      const activeStepId: string | number | undefined = this.activeStepId();

      untracked(() => {
        steps.forEach((step: StepComponent) => {
          if (step.stepId() === undefined) {
            step.setStepId(this.nextId++);
          }
        });

        if (activeStepId === undefined && steps.length > 0) {
          this.activeStepId.set(steps[0].stepId());
        }
      });
    });
  }

  getStepsCount(): number {
    return this.steps().length;
  }

  find(id: string | number): StepComponent | undefined {
    return this.steps().find((step: StepComponent): boolean => step.stepId() === id);
  }

  setActiveStepById(id: string | number): void {
    const step = this.find(id);
    if (step) {
      this.activeStepId.set(id);
    }
  }

  setActiveStep(step: StepComponent): void {
    const id = step.stepId();
    if (id !== undefined) {
      this.activeStepId.set(id);
    }
  }
}
